import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Send, Loader2, Bot, User, Phone, Ticket } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

type Msg = { role: "user" | "assistant"; content: string };

const OPENING =
  "Hi, I'm the Neil AI Support Assistant. I can help with AI Employees, Voice AI, PUDU Robotics, cleaning robots, service robots, mapping, training, or support tickets. What do you need help with today?";

const SupportAssistant = () => {
  const [messages, setMessages] = useState<Msg[]>([{ role: "assistant", content: OPENING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const next = [...messages, { role: "user" as const, content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-chat", {
        body: { messages: next.slice(-12), mode: "support" },
      });
      if (error) throw error;
      const reply = (data as any)?.message || (data as any)?.reply || "I'm having trouble right now. Please call (336) 203-3184 or open a ticket and our team will help.";
      setMessages([...next, { role: "assistant", content: reply }]);
    } catch {
      setMessages([...next, { role: "assistant", content: "Sorry — I couldn't reach the assistant. Please call (336) 203-3184 (24/7) or open a ticket and a specialist will follow up." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="AI Support Assistant | Neil AI" description="Chat with the Neil AI Support Assistant for help with AI Employees, voice agents, PUDU robotics, mapping, training, and tickets." canonical="https://www.neilai.ai/support/ai-assistant" />
      <div className="min-h-screen bg-[#05070A] text-white pb-24">
        <Navigation />
        <section className="pt-32 md:pt-40 pb-16">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-6">
              <Bot className="w-12 h-12 text-[#00F5A0] mx-auto mb-3" />
              <h1 className="text-3xl md:text-4xl font-bold">AI Support Assistant</h1>
              <p className="text-slate-300 mt-2">For serious or safety issues, call <a className="text-[#00F5A0] hover:underline" href="tel:+13362033184">(336) 203-3184</a>.</p>
            </div>

            <Card className="bg-[#0A1828]/60 border-primary/20 overflow-hidden flex flex-col" style={{ height: "60vh", minHeight: 420 }}>
              <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
                {messages.map((m, i) => (
                  <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                    {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-[#00F5A0]/15 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-[#00F5A0]" /></div>}
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-[#00A3FF] text-white" : "bg-[#05070A] border border-primary/20 text-slate-200"}`}>
                      {m.content}
                    </div>
                    {m.role === "user" && <div className="w-8 h-8 rounded-full bg-[#00A3FF]/15 flex items-center justify-center shrink-0"><User className="w-4 h-4 text-[#00A3FF]" /></div>}
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#00F5A0]/15 flex items-center justify-center"><Bot className="w-4 h-4 text-[#00F5A0]" /></div>
                    <div className="rounded-2xl px-4 py-2.5 bg-[#05070A] border border-primary/20 text-slate-400 text-sm">Thinking…</div>
                  </div>
                )}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-primary/20 p-3 flex gap-2">
                <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe what you need help with…" className="bg-[#05070A] border-primary/30" />
                <Button type="submit" disabled={loading || !input.trim()} className="bg-gradient-to-r from-[#00F5A0] to-[#00D9F5] text-[#05070A] font-bold">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </Card>

            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Button asChild variant="outline" className="border-primary/40"><Link to="/support/ticket"><Ticket className="w-4 h-4 mr-2" />Open a Ticket</Link></Button>
              <Button asChild variant="outline" className="border-primary/40"><a href="tel:+13362033184"><Phone className="w-4 h-4 mr-2" />Call (336) 203-3184</a></Button>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    </>
  );
};
export default SupportAssistant;
